import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Link } from "wouter";
import NoPermissions from "../authorization/NoPermissions";
import { getPayrollYears, getPayrollMonthsByYear, getPayrollListByMonth, deleteBusta } from "../services/api";
import { months } from "../config";
import BpCuTableEmployee from "./bp_cu_table_employee";


export default function BustePagaTab({ token, employee, profile, onPreview }) {
  const employeeId = employee?.id;
    
  const hasPermission =
    profile?.customer_type !== "employee" ||
    profile?.employee?.permissions.includes("hr.view_docs_ou") ||
    profile?.employee?.permissions.includes("hr.view_docs_all");

  const [yearsList, setYearsList] = useState([]);
  const [yearDetails, setYearDetails] = useState({});

  const [openYear, setOpenYear] = useState(null);

  const [loadingYears, setLoadingYears] = useState(false);

  useEffect(() => {
    if (!token || !employeeId || !hasPermission) return;

    const controller = new AbortController();
    const { signal } = controller;

    setLoadingYears(true);

    (async () => {
      try {
        const anniRaw = await getPayrollYears(
          token,
          null,
          "payrolls",
          employeeId,
          signal
        );

        const ordered = [...anniRaw].sort((a, b) => b.year - a.year);

        setYearsList(ordered);
        setLoadingYears(false);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
          return;
        }

        console.error("Errore caricamento:", err);

        setLoadingYears(false);

        Swal.fire({
          icon: "error",
          title: "Errore",
          text: "Errore nel caricamento",
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [token, employeeId, hasPermission]);

  async function loadYearIfNeeded(year) {
    // se quell'anno è già stato caricato e non sta caricando, non rifaccio la richiesta
    if (
      yearDetails[year] &&
      yearDetails[year].allDocs &&
      !yearDetails[year].loading
    ) {
      return;
    }

    setYearDetails(prev => ({
      ...prev,
      [year]: { loading: true, allDocs: prev[year]?.allDocs || [] }
    }));

    try {
      const monthsRaw = await getPayrollMonthsByYear(
        token,
        year,
        null,
        "payrolls",
        employeeId
      );

      const monthsExpanded = [];

      for (const m of monthsRaw) {
        const resp = await getPayrollListByMonth(
          token,
          year,
          m.month,
          null,
          "payrolls",
          employeeId
        );

        const list = Array.isArray(resp) ? resp : resp?.results || [];

        const mappedBuste = list.map((busta) => ({
          id: busta.id,
          title: `${String(busta.month).padStart(2, "0")} / ${busta.year}`,
          createdAt: busta.created_at,
          isRead: !!busta.read_at,
          readAt: busta.read_at,
          description: "",
          download: busta.download_link,
          download_link: busta.download_link,
          year: busta.year,
          month: busta.month,
          isConfirmed: !!busta.is_confirmed,
        }));

        monthsExpanded.push({
          month: m.month,
          buste: mappedBuste,
        });
      }

      monthsExpanded.sort((a, b) => b.month - a.month);

      const allDocs = monthsExpanded
        .flatMap((m) => m.buste)
        .sort((a, b) => {
          if (b.year !== a.year) return b.year - a.year;
          if (b.month !== a.month) return b.month - a.month;
          return b.id - a.id;
        });

      setYearDetails(prev => ({
        ...prev,
        [year]: { loading: false, allDocs }
      }));
    } catch (err) {
      setYearDetails(prev => ({
        ...prev,
        [year]: { loading: false, allDocs: prev[year]?.allDocs || [] }
      }));

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: `Errore nel caricamento delle buste paga ${year}`,
      });
    }
  }

  // cancellazione buste paga
  async function handleDeleteBustaLocal(docId, year) {
    const { isConfirmed } = await Swal.fire({
      title: "Sei sicuro?",
      text: "L’operazione non è reversibile.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sì, elimina",
      cancelButtonText: "Annulla",
    });

    if (!isConfirmed) return;

    await deleteBusta(token, docId, "payrolls");

    setYearDetails((prev) => {
      const yearData = prev[year];
      if (!yearData) return prev;

      const updatedDocs = yearData.allDocs.filter((d) => d.id !== docId);

      setYearsList((yl) =>
        yl
          .map((yy) => {
            if (yy.year !== year) return yy;
            const newCount = Math.max(0, Number(yy.count || 0) - 1);
            return { ...yy, count: newCount };
          })
          .filter((yy) => yy.year !== year || Number(yy.count || 0) > 0)
      );

      if (updatedDocs.length === 0) {
        const copy = { ...prev };
        delete copy[year];
        setOpenYear((prevOpen) => (prevOpen === year ? null : prevOpen));
        return copy;
      }

      return {
        ...prev,
        [year]: {
          ...yearData,
          allDocs: updatedDocs,
        },
      };
    });

    Swal.fire("Eliminato!", "", "success");
  }
  

  if (profile?.customer_type === "employee" && !hasPermission) {
    return <NoPermissions />;
  }

  return (
    <div className="pt-2 pb-4">
      <Link
        to={`/dipendenti/${employee.cf}/upload_busta`}
        className="relative inline-flex items-center rounded-sm bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        Carica Nuova
      </Link>

      <div className="mt-4">
        {loadingYears ? (
          <div className="text-sm italic text-gray-500 py-6 text-center">
            Caricamento...
          </div>
        ) : (
          <div className="divide-y divide-gray-200 border border-gray-200 rounded-md">
            {yearsList.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-6 italic">
                Nessuna busta paga caricata
              </div>
            )}

            {yearsList.map((y) => {
              const isOpen = openYear === y.year;
              const detail = yearDetails[y.year] || { loading: false, allDocs: [] };

              return (
                <div key={y.year}>
                  {/* HEADER ANNO */}
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-indigo-50"
                    onClick={async () => {
                      // se clicco lo stesso anno aperto → chiudo
                      setOpenYear((prev) => (prev === y.year ? null : y.year));

                      const wasClosed = openYear !== y.year;
                      if (wasClosed) {
                        await loadYearIfNeeded(y.year);
                      }
                    }}
                  >
                    <span className="text-base font-semibold text-gray-900">
                      {y.year}
                    </span>

                    <span className="text-xs text-gray-500">
                      {y.count} cedolin{y.count === 1 ? "o" : "i"}
                    </span>
                  </button>

                  {/* DETTAGLIO SOLO SE L'ANNO È APERTO */}
                  {isOpen && (
                    <div className="bg-indigo-50 px-4 py-3 overflow-x-auto">
                      {detail.loading ? (
                        <div className="text-sm italic text-gray-500 py-6 text-center">
                          Caricamento...
                        </div>
                      ) : (
                        <BpCuTableEmployee
                          type="bp"
                          documents={detail.allDocs}
                          onDelete={(docId) => handleDeleteBustaLocal(docId, y.year)}
                          onPreview={onPreview}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}